import { useEffect, useMemo, useState, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DropZone } from "@/components/upload/DropZone";
import { api } from "@/services/api";
import { toast } from "@/hooks/use-toast";
import { Upload as UploadIcon } from "lucide-react";
import { SEO, SEOConfigs } from "@/components/SEO";
import { useAuth } from "@/contexts/AuthContext";
import { Label } from "@/components/ui/label";

export function Upload() {
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const pollingRefs = useRef<{ [jobId: string]: NodeJS.Timeout }>({});
  const { user } = useAuth();
  const [ediSenderId, setEdiSenderId] = useState("");
  const [ediReceiverId, setEdiReceiverId] = useState("");
  type LabeledId = { label: string; id: string };
  const [savedSenderOptions, setSavedSenderOptions] = useState<LabeledId[]>([]);
  const [savedReceiverOptions, setSavedReceiverOptions] = useState<LabeledId[]>(
    []
  );

  useEffect(() => {
    // Load saved/labeled IDs from user settings
    const loadInterchangeIds = async () => {
      try {
        const ids = await api.getSavedInterchangeIds();
        if (ids.success) {
          const senders = (ids.data?.savedSenderIds || [])
            .map((x: any) => ({
              label: x.label as string,
              id: (x.interchangeId as string).slice(0, 15),
            }))
            .sort(
              (
                a: { label: string; id: string },
                b: { label: string; id: string }
              ) => {
                const la = a.label.toLowerCase();
                const lb = b.label.toLowerCase();
                if (la < lb) {
                  return -1;
                }
                if (la > lb) {
                  return 1;
                }
                const ia = a.id.toLowerCase();
                const ib = b.id.toLowerCase();
                if (ia < ib) {
                  return -1;
                }
                if (ia > ib) {
                  return 1;
                }
                return 0;
              }
            );
          const receivers = (ids.data?.savedReceiverIds || [])
            .map((x: any) => ({
              label: x.label as string,
              id: (x.interchangeId as string).slice(0, 15),
            }))
            .sort(
              (
                a: { label: string; id: string },
                b: { label: string; id: string }
              ) => {
                const la = a.label.toLowerCase();
                const lb = b.label.toLowerCase();
                if (la < lb) {
                  return -1;
                }
                if (la > lb) {
                  return 1;
                }
                const ia = a.id.toLowerCase();
                const ib = b.id.toLowerCase();
                if (ia < ib) {
                  return -1;
                }
                if (ia > ib) {
                  return 1;
                }
                return 0;
              }
            );
          setSavedSenderOptions(senders);
          setSavedReceiverOptions(receivers);

          // Set default values to first available option or SNAPLADING
          const defaultSender = (senders[0]?.id || "SNAPLADING").slice(0, 15);
          const defaultReceiver = (receivers[0]?.id || "SNAPLADING").slice(
            0,
            15
          );
          setEdiSenderId(defaultSender);
          setEdiReceiverId(defaultReceiver);
        } else {
          setEdiSenderId("SNAPLADING");
          setEdiReceiverId("SNAPLADING");
        }
      } catch {
        setEdiSenderId("SNAPLADING");
        setEdiReceiverId("SNAPLADING");
      }
    };
    loadInterchangeIds();
  }, []);

  const handleDrop = (newFiles: File[]) => {
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleRemove = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async (e?: React.MouseEvent) => {
    // Prevent any default behavior that might cause page reload
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    console.log("Upload function called");
    if (files.length === 0) {
      toast({
        title: "No files selected",
        description: "Please select at least one file to upload.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);

    try {
      console.log("Starting upload process...");
      console.log(
        "Files to upload:",
        files.map((f) => ({ name: f.name, size: f.size }))
      );

      if (!ediSenderId || !ediReceiverId) {
        toast({
          title: "Missing IDs",
          description: "Sender and Receiver IDs are required.",
          variant: "destructive",
        });
        return;
      }
      const response = await api.processImages(
        files,
        ediSenderId.slice(0, 15),
        ediReceiverId.slice(0, 15)
      );
      console.log("Upload response:", response);

      const jobId = response.Data?.jobId;
      toast({
        title: "Images sent for processing",
        description: "Your images have been uploaded and are being processed.",
        duration: 3000,
      });
      if (jobId) {
        pollJobStatus(jobId.toString());
      }
      setFiles([]);
    } catch (err: any) {
      console.error("Upload failed:", err);
      console.error("Error details:", {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
      });

      toast({
        title: "Upload failed",
        description:
          err.response?.data?.message ||
          err.message ||
          "02 There was a problem uploading your images. Please try again.",
        variant: "destructive",
        duration: 0,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const pollJobStatus = (jobId: string) => {
    const poll = async () => {
      try {
        const res = await api.getJobStatus(jobId);
        // When filtering by jobId, the response structure is { Success: true, Data: { jobs: [...], total: 1 } }
        const job = res.Data?.jobs?.find(
          (j: any) => j.JobId.toString() === jobId
        );
        if (!job) return;
        if (job.Status === "Completed") {
          // Success toast
          toast({
            title: "Image processed",
            description: (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <img
                  src={job.ImageUrls?.[0]}
                  alt="thumbnail"
                  style={{
                    width: 32,
                    height: 32,
                    objectFit: "cover",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                  }}
                />
                <span>
                  {job.ExtractedData?.Shipper?.Name || "-"} |{" "}
                  {job.ExtractedData?.References?.BolNumber ||
                    job.ExtractedData?.References?.ISA ||
                    "-"}
                </span>
              </div>
            ),
            duration: 3000,
          });
          clearInterval(pollingRefs.current[jobId]);
        } else if (job.Status === "Failed") {
          // Failure toast with persistent dismiss, details, and reprocess
          toast({
            title: "Processing failed",
            description: (
              <div>
                <span>03 There was a problem processing your image.</span>
                <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                  <Button size="sm" onClick={() => openDetailsModal(jobId)}>
                    Show Details
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => reprocessJob(jobId)}
                  >
                    Reprocess
                  </Button>
                </div>
              </div>
            ),
            variant: "destructive",
            duration: 0, // Persistent
          });
          clearInterval(pollingRefs.current[jobId]);
        }
      } catch (err) {
        // Optionally handle polling error
      }
    };
    pollingRefs.current[jobId] = setInterval(poll, 5000);
  };

  const openDetailsModal = (jobId: string) => {
    window.location.href = `/documents?jobId=${jobId}&showDetails=1`;
  };

  const reprocessJob = async (jobId: string) => {
    try {
      await api.reprocessJob(jobId);
      toast({
        title: "Reprocess requested",
        description: "The job has been requeued for processing.",
        duration: 3000,
      });
      pollJobStatus(jobId);
    } catch (err) {
      toast({
        title: "Reprocess failed",
        description: "Could not requeue the job.",
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  return (
    <>
      <SEO {...SEOConfigs.upload} url="/app/upload" />
      <div className="container mx-auto py-8">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UploadIcon className="h-6 w-6" />
                Upload Bill of Lading Images
              </CardTitle>
              <CardDescription>
                Upload one or more scanned images of a bill of lading to convert
                them to EDI X12 211 or 204 format.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <select
                    id="ediSenderId"
                    value={ediSenderId}
                    onChange={(e) => {
                      const selectedId = e.target.value.slice(0, 15);
                      setEdiSenderId(selectedId);
                    }}
                    className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {!savedSenderOptions.some((o) => o.id === ediSenderId) &&
                      ediSenderId && (
                        <option value={ediSenderId}>{ediSenderId}</option>
                      )}
                    {savedSenderOptions.map((o) => (
                      <option key={`${o.label}:${o.id}`} value={o.id}>
                        {`${o.label} [${o.id}]`}
                      </option>
                    ))}
                  </select>
                  <Label className="block" htmlFor="ediSenderId">
                    Interchange Sender ID
                  </Label>
                </div>
                <div className="space-y-2">
                  <select
                    id="ediReceiverId"
                    value={ediReceiverId}
                    onChange={(e) => {
                      const selectedId = e.target.value.slice(0, 15);
                      setEdiReceiverId(selectedId);
                    }}
                    className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {!savedReceiverOptions.some(
                      (o) => o.id === ediReceiverId
                    ) &&
                      ediReceiverId && (
                        <option value={ediReceiverId}>{ediReceiverId}</option>
                      )}
                    {savedReceiverOptions.map((o) => (
                      <option key={`${o.label}:${o.id}`} value={o.id}>
                        {`${o.label} [${o.id}]`}
                      </option>
                    ))}
                  </select>
                  <Label className="block" htmlFor="ediReceiverId">
                    Interchange Receiver ID
                  </Label>
                </div>
              </div>
              <DropZone
                onDrop={handleDrop}
                files={files}
                onRemove={handleRemove}
                isUploading={isUploading}
              />
              <div className="flex justify-end space-x-4">
                <Button
                  variant="outline"
                  onClick={() => setFiles([])}
                  disabled={files.length === 0}
                >
                  Clear
                </Button>
                <Button
                  type="button"
                  onClick={handleUpload}
                  disabled={files.length === 0 || isUploading}
                >
                  {isUploading ? "Processing..." : "Upload & Generate EDI"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
